import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import SecurityBadges from './components/SecurityBadges';
import RoleSelector from './components/RoleSelector';
import LoginForm from './components/LoginForm';
import MFAOptions from './components/MFAOptions';
import AuditInfo from './components/AuditInfo';

const Login = () => {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState('dentist');
  const [showMFA, setShowMFA] = useState(false);
  const [loginData, setLoginData] = useState(null);

  const handleLoginSubmit = (formData) => {
    setLoginData(formData);
    setShowMFA(true);
  };

  const handleMFAVerify = () => {
    const dashboardRoutes = {
      dentist: '/patient-dashboard',
      hygienist: '/patient-dashboard',
      admin: '/billing-management'
    };
    
    navigate(dashboardRoutes?.[selectedRole] || '/patient-dashboard');
  };

  const handleForgotPassword = () => {
    alert('Password recovery workflow would be initiated here with secure email verification.');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left Section - Branding & Info */}
          <div className="flex flex-col justify-center space-y-6 md:space-y-8">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-14 h-14 md:w-16 md:h-16 bg-primary/10 rounded-xl">
                  <Icon name="Activity" size={32} color="var(--color-primary)" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">
                    Dental Systems
                  </h1>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Practice Management System
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4 md:space-y-6">
              <div className="space-y-3">
                <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                  Secure Dental Practice Access
                </h2>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  Comprehensive dental practice management system with HIPAA-compliant authentication, 
                  complete patient dental records, treatment planning, and integrated clinical workflows.
                </p>
              </div>

              <div className="space-y-3">
                {[
                  { icon: 'Shield', text: 'HIPAA Compliant Security' },
                  { icon: 'Lock', text: 'End-to-End Encryption' },
                  { icon: 'FileText', text: 'Complete Audit Trails' },
                  { icon: 'Users', text: 'Role-Based Access Control' }
                ]?.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-success/10 rounded-lg flex-shrink-0">
                      <Icon name={feature?.icon} size={16} color="var(--color-success)" />
                    </div>
                    <span className="text-sm md:text-base text-foreground">
                      {feature?.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <SecurityBadges />
          </div>

          {/* Right Section - Login Form */}
          <div className="flex items-center justify-center">
            <div className="w-full max-w-md">
              <div className="bg-card rounded-2xl shadow-elevation-4 border border-border p-6 md:p-8 space-y-6">
                <div className="text-center space-y-2">
                  <h3 className="text-xl md:text-2xl font-semibold text-foreground">
                    {showMFA ? 'Verify Your Identity' : 'Sign In to Your Account'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {showMFA 
                      ? 'Complete multi-factor authentication to proceed'
                      : 'Access your healthcare management dashboard'
                    }
                  </p>
                </div>

                {!showMFA ? (
                  <>
                    <RoleSelector 
                      selectedRole={selectedRole}
                      onRoleChange={setSelectedRole}
                    />
                    
                    <LoginForm
                      selectedRole={selectedRole}
                      onSubmit={handleLoginSubmit}
                      onForgotPassword={handleForgotPassword}
                    />
                  </>
                ) : (
                  <MFAOptions
                    onVerify={handleMFAVerify}
                    onCancel={() => setShowMFA(false)}
                  />
                )}
              </div>

              <div className="mt-6">
                <AuditInfo />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
            <p>&copy; {new Date()?.getFullYear()} Dental Systems. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <button className="hover:text-foreground transition-smooth">Privacy Policy</button>
              <span>&middot;</span>
              <button className="hover:text-foreground transition-smooth">Terms of Service</button>
              <span>&middot;</span>
              <button className="hover:text-foreground transition-smooth">Support</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;